import { Tree, Card } from 'antd';
import { useMemo } from 'react';
import { type ConfiguratorModuleSpecsFragment } from '../../../../api/fragments/ConfiguratorModuleSpecs';
// eslint-disable-next-line max-len
import { type FinderApplicationPrivateModuleSpecsFragment } from '../../../../api/fragments/FinderApplicationPrivateModuleSpecs';
// eslint-disable-next-line max-len
import { type FinderApplicationPublicModuleSpecsFragment } from '../../../../api/fragments/FinderApplicationPublicModuleSpecs';
import { type StandardApplicationModuleSpecsFragment } from '../../../../api/fragments/StandardApplicationModuleSpecs';
import { useMultipleDealerIds } from '../../../../components/contexts/DealerContextManager';
import NoItemResult from '../../../../components/results/NoItemResult';
import useTranslatedString from '../../../../utilities/useTranslatedString';
import DealerSelectorEmpty from './DealerSelectorEmpty';

export type UpdateVehicleAssignmentsValue = {
    dealerVehicles: Record<string, string[]>;
};

export type ApplicationModules =
    | StandardApplicationModuleSpecsFragment
    | ConfiguratorModuleSpecsFragment
    | FinderApplicationPublicModuleSpecsFragment
    | FinderApplicationPrivateModuleSpecsFragment;

export type VehicleAssignmentsProps = {
    module: ApplicationModules;
    noItemText: string;
};

const ApplicationModuleVehicleAssignments = ({ module, noItemText }: VehicleAssignmentsProps) => {
    const translatedString = useTranslatedString();

    const { dealerIds } = useMultipleDealerIds();

    const treeData = useMemo(
        () =>
            module.dealerVehicles
                .filter(({ dealer }) => dealerIds.includes(dealer.id))
                .map(({ dealer, vehicles }) => ({
                    key: dealer.id,
                    title: translatedString(dealer.legalName),
                    children: vehicles.map(vehicle => ({
                        key: `${dealer.id}-${vehicle.id}`,
                        title: translatedString(vehicle.name),
                    })),
                })),
        [module, translatedString, dealerIds]
    );

    if (dealerIds.length === 0) {
        return <DealerSelectorEmpty />;
    }

    if (treeData.length === 0) {
        return <NoItemResult subTitle={noItemText} />;
    }

    return (
        <Card>
            <Tree selectable={false} treeData={treeData} defaultExpandAll />
        </Card>
    );
};

export default ApplicationModuleVehicleAssignments;
